import { Link } from "react-router-dom";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { LifeBuoy, Mail, MessageCircle, ArrowRight } from "lucide-react";
import useSEO from "../lib/useSEO";

const SECTIONS = [
  {
    eyebrow: "Premiers pas",
    title: "Découvrir la plateforme",
    items: [
      {
        q: "Qu'est-ce qu'EventMarket Pro ?",
        a: "EventMarket Pro est une marketplace française qui réunit organisateurs d'événements, exposants, propriétaires de lieux et prestataires. Vous pouvez y trouver un événement où exposer, louer un stand, réserver un lieu ou faire appel à un prestataire — le tout avec un paiement sécurisé et une commission plateforme transparente.",
      },
      {
        q: "À qui s'adresse la plateforme ?",
        a: "Aux organisateurs de salons, foires, marchés et festivals ; aux exposants (artisans, producteurs, marques) ; aux propriétaires de lieux atypiques ou professionnels ; aux prestataires de services événementiels (traiteurs, son, lumière, mobilier).",
      },
      {
        q: "L'inscription est-elle gratuite ?",
        a: "Oui. La création de compte est gratuite, qu'il s'agisse d'un visiteur, organisateur, exposant, propriétaire de lieu ou prestataire. Nous prélevons uniquement une commission sur les transactions réalisées via la plateforme.",
      },
    ],
  },
  {
    eyebrow: "Organisateurs",
    title: "Créer et gérer un événement",
    items: [
      {
        q: "Comment créer mon premier événement ?",
        a: "Une fois connecté en tant qu'organisateur, allez dans votre tableau de bord et cliquez sur « Nouvel événement ». Renseignez le titre, la description, les dates, la ville et téléversez quelques photos. Vous pouvez ensuite ajouter des stands (booths) avec leur prix, surface et options.",
      },
      {
        q: "Combien coûte la mise en ligne d'un événement ?",
        a: "La publication est gratuite. Nous prélevons une commission de 20% sur chaque réservation de stand encaissée via la plateforme. Aucun frais fixe, aucun engagement.",
      },
      {
        q: "Puis-je booster mon événement pour gagner en visibilité ?",
        a: "Oui. Depuis votre tableau de bord, choisissez l'événement à mettre en avant et sélectionnez un pack Boost. Vous recevrez par email la confirmation, un suivi mi-parcours et une alerte avant expiration.",
      },
    ],
  },
  {
    eyebrow: "Exposants",
    title: "Réserver un stand",
    items: [
      {
        q: "Comment réserver un stand ?",
        a: "Sur la fiche d'un événement, choisissez le type de stand (taille, options) et cliquez sur « Réserver ». Vous serez redirigé vers une page de paiement sécurisée Stripe. Une fois le paiement validé, vous recevez la confirmation par email et une facture PDF téléchargeable depuis votre tableau de bord.",
      },
      {
        q: "Quels sont les moyens de paiement acceptés ?",
        a: "Toutes les cartes bancaires majeures (Visa, Mastercard, American Express) via Stripe. Les paiements sont chiffrés et conformes à la norme PCI-DSS.",
      },
      {
        q: "Puis-je annuler ou être remboursé ?",
        a: "Les conditions d'annulation sont définies par chaque organisateur et précisées sur la fiche événement. En cas de litige, notre service support peut intervenir. Voir nos CGV pour les détails.",
      },
    ],
  },
  {
    eyebrow: "Paiements & sécurité",
    title: "Transactions et données",
    items: [
      {
        q: "Mes paiements sont-ils sécurisés ?",
        a: "Oui. Tous les paiements transitent par Stripe, leader mondial du paiement en ligne, certifié PCI-DSS niveau 1. Aucune donnée bancaire n'est stockée sur nos serveurs.",
      },
      {
        q: "Comment sont protégées mes données personnelles ?",
        a: "Nous appliquons le RGPD : vos données sont stockées en UE, vous pouvez à tout moment demander l'accès, la rectification ou la suppression depuis votre profil. Consultez notre Politique de confidentialité pour le détail.",
      },
      {
        q: "Comment gérer mes préférences email ?",
        a: "Depuis votre profil, rubrique « Préférences email », vous pouvez vous désabonner par catégorie (newsletter, alertes événements, transactions). Les emails légaux ou liés à vos achats restent obligatoires.",
      },
    ],
  },
  {
    eyebrow: "Support",
    title: "Vous avez encore une question ?",
    items: [
      {
        q: "Comment contacter l'équipe ?",
        a: "Utilisez notre formulaire de contact en bas de page, écrivez-nous à hello@eventmarketpro.fr ou appelez-nous au 06 65 02 87 00 (horaires de bureau, jours ouvrés).",
      },
      {
        q: "Quel est le délai de réponse moyen ?",
        a: "Nous répondons sous 24h ouvrées pour les demandes courantes, et sous 4h pour les urgences (paiement bloqué, accès compte).",
      },
    ],
  },
];

export default function Faq() {
  useSEO({
    title: "Aide & FAQ",
    description:
      "Questions fréquentes sur EventMarket Pro : créer un événement, réserver un stand, paiements sécurisés, RGPD, support. Toutes les réponses pour bien démarrer.",
  });

  return (
    <div className="bg-cream min-h-[calc(100vh-80px)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="flex items-center gap-2 mb-3">
          <LifeBuoy className="h-5 w-5 text-accent" />
          <p className="text-label text-muted-foreground">Centre d&apos;aide</p>
        </div>
        <h1
          data-testid="faq-title"
          className="font-heading font-light tracking-editorial text-4xl sm:text-5xl lg:text-6xl text-ink mb-6 leading-[0.95]"
        >
          Toutes vos questions,<br />
          <span className="font-display italic text-accent">une réponse claire.</span>
        </h1>
        <p className="text-ink/70 font-light text-lg max-w-2xl mb-12 leading-relaxed">
          Compilation des questions les plus fréquentes posées par notre communauté.
          Vous ne trouvez pas votre réponse ? <Link to="/contact" className="text-accent underline hover:text-ink">Contactez-nous</Link>.
        </p>

        <div className="space-y-16">
          {SECTIONS.map((sec, sIdx) => (
            <section key={sIdx} data-testid={`faq-section-${sIdx}`}>
              <div className="flex items-baseline gap-3 mb-5 pb-3 border-b border-ink/10">
                <p className="text-label text-accent">{sec.eyebrow}</p>
                <h2 className="font-heading text-2xl text-ink font-light tracking-editorial">{sec.title}</h2>
              </div>
              <Accordion type="single" collapsible className="w-full">
                {sec.items.map((it, i) => (
                  <AccordionItem key={i} value={`item-${sIdx}-${i}`} className="border-ink/10">
                    <AccordionTrigger
                      data-testid={`faq-q-${sIdx}-${i}`}
                      className="text-left text-[15px] font-medium text-ink py-5 hover:no-underline hover:text-accent transition-colors"
                    >
                      {it.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[14.5px] leading-relaxed text-ink/75 font-light pb-5 pr-8">
                      {it.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          ))}
        </div>

        {/* CTA contact */}
        <div className="mt-20 bg-ink text-cream rounded-md p-10 grain">
          <p className="text-label text-accent mb-3">Toujours bloqué ?</p>
          <h3 className="font-heading text-3xl font-light tracking-editorial mb-4 leading-tight">
            Notre équipe est <span className="font-display italic text-accent">à votre écoute</span>.
          </h3>
          <p className="text-cream/70 font-light mb-8 max-w-lg">
            Posez votre question — on vous répond sous 24h ouvrées.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" data-testid="faq-cta-contact" className="inline-flex items-center gap-2 bg-accent text-ink px-6 py-3 text-sm font-semibold tracking-wide hover:bg-accent/90 transition">
              <MessageCircle className="h-4 w-4" /> Formulaire de contact <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="mailto:hello@eventmarketpro.fr" className="inline-flex items-center gap-2 border border-cream/30 text-cream px-6 py-3 text-sm font-semibold tracking-wide hover:bg-cream/10 transition">
              <Mail className="h-4 w-4" /> hello@eventmarketpro.fr
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
